import { useState, useEffect } from 'react';
import { hotfaceAPI, notificationAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Flame,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Heart,
  MessageCircle,
  Share2,
  ExternalLink,
  Trash2,
  Send,
  Mail,
  Bell,
  CheckCircle2,
  AlertCircle,
  Copy,
  CheckCheck,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { adminAPI } from '../lib/api';

const EngagementBadge = ({ level }) => {
  const config = {
    high: { label: 'Cao', className: 'bg-[#EBF5ED] text-[#1E7132]', icon: TrendingUp },
    medium: { label: 'Trung bình', className: 'bg-[#FEF9E6] text-[#8F6702]', icon: Minus },
    low: { label: 'Thấp', className: 'bg-[#FCE9EC] text-[#A8112A]', icon: TrendingDown },
  };
  
  const { label, className, icon: BadgeIcon } = config[level] || config.low;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${className}`}>
      <BadgeIcon size={12} />
      {label}
    </span>
  );
};

export default function Hotface() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendChannels, setSendChannels] = useState({ email: true, telegram: false });
  const [copiedId, setCopiedId] = useState(null);
  const { isAdmin } = useAuth();
  const [addingToPoolId, setAddingToPoolId] = useState(null);
  const [addingBulk, setAddingBulk] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await hotfaceAPI.getList();
      setLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch hotface links:', error);
      toast.error('Không thể tải danh sách Hotface');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(links.map(l => l.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
    }
  };

  const openDeleteDialog = (id) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    
    setDeleting(true);
    try {
      await hotfaceAPI.delete(deletingId);
      setLinks(prev => prev.filter(l => l.id !== deletingId));
      setSelectedIds(prev => prev.filter(i => i !== deletingId));
      toast.success('Đã xóa khỏi Hotface List');
    } catch (error) {
      toast.error('Xóa thất bại');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const handleSendNotifications = async () => {
    if (selectedIds.length === 0) {
      toast.error('Vui lòng chọn ít nhất 1 link');
      return;
    }

    const channels = [];
    if (sendChannels.email) channels.push('email');
    if (sendChannels.telegram) channels.push('telegram');

    if (channels.length === 0) {
      toast.error('Vui lòng chọn ít nhất 1 kênh gửi');
      return;
    }

    setSending(true);
    try {
      const response = await notificationAPI.send(selectedIds, channels);
      toast.success(`Đã gửi ${response.data.logs?.length || 0} thông báo!`);
      setSendDialogOpen(false);
      setSelectedIds([]);
      fetchLinks(); // Refresh to update sent status
    } catch (error) {
      toast.error('Gửi thông báo thất bại');
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Đã sao chép liên kết!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Không thể sao chép liên kết');
    }
  };

  const handleAddToPool = async (link) => {
    setAddingToPoolId(link.id);
    try {
      await adminAPI.addFromAutoResult(
        link.url, 
        link.gender || 'both', 
        `Added from ${link.id} by admin`
      );
      toast.success('Đã thêm vào Nguồn chung thành công!');
    } catch (error) {
      console.error('Failed to add to pool:', error);
      toast.error('Không thể thêm vào Nguồn chung');
    } finally {
      setAddingToPoolId(null);
    }
  };

  const handleBulkAddToPool = async () => {
    if (selectedIds.length === 0) return;
    
    setAddingBulk(true);
    let successCount = 0;
    
    const selectedLinks = links.filter(l => selectedIds.includes(l.id));
    
    try {
      // Loop through and add, but we could optimize with a more specialized API later
      for (const link of selectedLinks) {
        try {
          await adminAPI.addFromAutoResult(
            link.url, 
            link.gender || 'both', 
            `Bulk added from Hotface List`
          );
          successCount++;
        } catch (e) {
          console.error(`Failed to add link ${link.id}:`, e);
        }
      }
      
      toast.success(`Đã thêm thành công ${successCount}/${selectedIds.length} link vào Nguồn chung!`);
      setSelectedIds([]); // Clear selection after bulk action
    } catch (error) {
      console.error('Bulk add error:', error);
      toast.error('Có lỗi xảy ra khi thêm hàng loạt');
    } finally {
      setAddingBulk(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-[#1877F2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hotface-page">
      {/* Header */}
      <div className="flex items-center justify-between animate-fadeIn">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
            <Flame className="text-[#F5C33B]" />
            Hotface List
          </h1>
          <p className="text-[#606770] mt-1">
            Danh sách các link Facebook có tương tác cao
          </p>
        </div>
        {selectedIds.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto mt-4 sm:mt-0">
            <Button
              onClick={() => setSendDialogOpen(true)}
              className="bg-[#1877F2] hover:bg-[#166FE5] w-full sm:w-auto"
              data-testid="send-notifications-btn"
            >
              <Send size={18} className="mr-2" />
              Gửi thông báo ({selectedIds.length})
            </Button>
            {isAdmin && (
              <Button
                onClick={handleBulkAddToPool}
                variant="outline"
                className="text-[#1877F2] border-[#1877F2]/20 hover:bg-[#E7F3FF] hover:text-[#1877F2] w-full sm:w-auto font-semibold"
                disabled={addingBulk}
              >
                {addingBulk ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Globe size={18} className="mr-2" />}
                Thêm vào Nguồn ({selectedIds.length})
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Links List */}
      <Card className="animate-fadeIn stagger-1" data-testid="hotface-list">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">
              {links.length} links đã lưu
            </CardTitle>
            {links.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="selectAll"
                  checked={selectedIds.length === links.length}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <label htmlFor="selectAll" className="text-sm text-[#606770]">
                  Chọn tất cả
                </label>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {links.length > 0 ? (
            <div className="space-y-3">
              {links.map((link) => (
                <div 
                  key={link.id}
                  className={`flex items-start gap-3 p-4 rounded-2xl border transition-all ${
                    selectedIds.includes(link.id) 
                      ? 'bg-[#E7F3FF] border-[#1877F2]' 
                      : 'bg-[#F7F8FA] border-transparent hover:bg-[#F0F2F5]'
                  }`}
                  data-testid={`hotface-item-${link.id}`}
                >
                  <div className="flex-shrink-0 pt-1">
                    <Checkbox
                      checked={selectedIds.includes(link.id)}
                      onCheckedChange={(checked) => handleSelect(link.id, checked)}
                      data-testid={`checkbox-${link.id}`}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 overflow-hidden">
                      <a 
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="url-text hover:underline flex items-center gap-1.5 text-sm text-[#1877F2] font-medium flex-1 overflow-hidden"
                      >
                        <span className="truncate flex-1 font-semibold">{link.title || link.url}</span>
                        <ExternalLink size={14} className="shrink-0" />
                      </a>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-[11px] text-[#606770]">
                      <span className="bg-white px-2 py-0.5 rounded border border-gray-100">
                        {new Date(link.saved_at).toLocaleString('vi-VN')}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Heart size={12} className="text-[#E41E3F]" /> {link.likes.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle size={12} className="text-[#1877F2]" /> {link.comments.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Share2 size={12} className="text-[#31A24C]" /> {link.shares.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E4E6EB] gap-2">
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xl font-black text-[#1C1E21] leading-none">
                            {link.engagement_score}
                          </p>
                          <p className="text-[9px] uppercase font-bold text-[#8C939D] mt-1">điểm</p>
                        </div>
                        <EngagementBadge level={link.engagement_level} />
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(link.url, link.id)}
                          className="h-9 w-9 p-0 rounded-lg border-gray-200"
                          title="Sao chép link"
                        >
                          {copiedId === link.id ? <CheckCheck size={16} className="text-[#31A24C]" /> : <Copy size={16} />}
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-[#1877F2] hover:text-[#1877F2] hover:bg-[#E7F3FF] h-9 px-3 rounded-lg border-gray-200 font-semibold"
                            onClick={() => handleAddToPool(link)}
                            disabled={addingToPoolId === link.id}
                            title="Thêm vào Nguồn chung (Pool)"
                          >
                            {addingToPoolId === link.id ? (
                               <Loader2 size={15} className="animate-spin mr-1.5" />
                            ) : (
                               <Globe size={15} className="mr-1.5" />
                            )}
                            {addingToPoolId === link.id ? 'Đang thêm...' : 'vào Nguồn chung'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[#E41E3F] hover:text-[#E41E3F] hover:bg-[#FCE9EC] h-9 px-3 rounded-lg border-gray-200 font-semibold"
                          onClick={() => openDeleteDialog(link.id)}
                          data-testid={`delete-btn-${link.id}`}
                        >
                          <Trash2 size={15} className="mr-1.5" />
                          Xóa
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Flame size={48} className="empty-state-icon mx-auto text-[#8C939D]" />
              <h3 className="empty-state-title">Chưa có link nào</h3>
              <p className="empty-state-text">
                Phân tích và lưu các link có tương tác cao vào đây
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#E41E3F]">
              <AlertCircle size={20} />
              Xác nhận xóa
            </DialogTitle>
            <DialogDescription>
              Bạn có chắc chắn muốn xóa link này khỏi Hotface List?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Hủy
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Đang xóa...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Xóa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Notifications Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent data-testid="send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send size={20} className="text-[#1877F2]" />
              Gửi thông báo
            </DialogTitle>
            <DialogDescription>
              Chọn kênh để gửi thông báo về {selectedIds.length} link đã chọn
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <Checkbox
                id="emailChannel"
                checked={sendChannels.email}
                onCheckedChange={(checked) => setSendChannels(prev => ({ ...prev, email: checked }))}
                data-testid="email-channel-checkbox"
              />
              <Mail size={20} className="text-[#1877F2]" />
              <div className="flex-1">
                <label htmlFor="emailChannel" className="font-medium">Email</label>
                <p className="text-sm text-[#606770]">Gửi qua email đã cài đặt</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <Checkbox
                id="telegramChannel"
                checked={sendChannels.telegram}
                onCheckedChange={(checked) => setSendChannels(prev => ({ ...prev, telegram: checked }))}
                data-testid="telegram-channel-checkbox"
              />
              <Bell size={20} className="text-[#1877F2]" />
              <div className="flex-1">
                <label htmlFor="telegramChannel" className="font-medium">Telegram</label>
                <p className="text-sm text-[#606770]">Gửi qua Telegram Bot</p>
              </div>
            </div>

            <div className="p-3 bg-[#FEF9E6] rounded-lg text-sm text-[#8F6702]">
              <strong>Lưu ý:</strong> Đảm bảo bạn đã cài đặt Email/Telegram trong phần Cài đặt trước khi gửi.
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setSendDialogOpen(false)}
              disabled={sending}
            >
              Hủy
            </Button>
            <Button 
              onClick={handleSendNotifications}
              className="bg-[#1877F2] hover:bg-[#166FE5]"
              disabled={sending}
              data-testid="confirm-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Đang gửi...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Gửi thông báo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
